import { FunctionalComponent, h } from '@stencil/core';
import { VNode } from '@stencil/core/internal/stencil-public-runtime';
import {
  FluidIconConfig,
  FluidIconKey,
  FluidSize,
  FluidTheme,
} from '@lmig/fluid-core';
import { FluidFunctionalComponentProps } from '../model/functional/fluid-functional-component-props.interface';
import { themeVar } from '../utils/themes/theme.variables';

// ================================================================ //
// -- Close Button

export interface CloseButtonProps extends FluidFunctionalComponentProps {
  render?: boolean; // True by default
  title?: string; // Optional - sets "title" attr & "aria-label"
  size?: FluidSize; // Optional - overrides default size for theme
  scale?: number; // Optional - scales Icon down (if necessary)
  small?: boolean; // Optional - decreases overall Button size (NOT Icon size)
  setRef?: Function;
  onClick?: Function;
  disabled?: boolean;
  theme: FluidTheme; // Required - necessary for default Icon size
  identifier?: string;
}

/**
 * Embedded Close / Dismiss Button
 *
 * Add class ".close-button" (or custom class name) to consuming
 * component's styles & apply ".close-button()" mixin (found in
 * "fluid-common-components.mixin").
 *
 * NOTE: "color" property is intentionally NOT included in the Icon
 * config - instead, Button's "inheritIconColor" internal Prop is used
 * so that the parent component's / element's styles can set Icon color.
 */
export const CloseButton: FunctionalComponent<CloseButtonProps> = (
  props: CloseButtonProps
) => {
  const {
    title,
    size,
    scale,
    small,
    setRef,
    onClick,
    disabled,
    theme,
    identifier,
  } = props;
  const render = props.render ?? true;
  const iconSize = size ?? themeVar<FluidSize>(theme, 'icons.default_size');

  return (
    render && (
      <fluid-button
        data-test-id={`${identifier}-close-button`}
        title={title ?? 'Close'}
        aria-label={title ?? 'Close'}
        disabled={disabled}
        theme={theme}
        overrideGlobalTheme={true}
        ref={(el) => !!setRef && setRef(el)}
        onClick={(event) => !!onClick && onClick(event)}
        inheritIconColor={true}
        small={small}
        icon={{
          key: FluidIconKey.DISMISS,
          size: iconSize,
          scale: scale ?? (!!small ? 30 : undefined),
        }}
        class={{
          'close-button': true,
          [`${props.class}`]: !!props.class,
        }}
      />
    )
  );
};

// ================================================================ //
// -- Collapse Toggle

interface CollapseToggleProps extends FluidFunctionalComponentProps {
  collapsed: boolean;
  toggleCollapsed: () => void;
  theme: FluidTheme;
}

/**
 * Renders a FLUID Icon Button with expand/collapse Icon
 */
export const CollapseToggle: FunctionalComponent<CollapseToggleProps> = (
  props: CollapseToggleProps
) => {
  const { collapsed, toggleCollapsed, theme } = props;
  const iconConfig: FluidIconConfig = {
    key: collapsed ? FluidIconKey.EXPAND : FluidIconKey.COLLAPSE,
    theme,
  };

  return (
    <fluid-button
      icon={iconConfig}
      onClick={toggleCollapsed}
      theme={theme}
      overrideGlobalTheme={true}
      class={{
        'collapse-toggle-btn': true,
        [`${props.class}`]: !!props.class,
      }}
    />
  );
};

// ================================================================ //
// -- Aria Button

export interface AriaButtonProps extends FluidFunctionalComponentProps {
  onClick?: Function;
  tabIndex?: number;
  disabled?: boolean;
  title?: string;
  identifier?: string;
}

export const AriaButton: FunctionalComponent<AriaButtonProps> = (
  props: AriaButtonProps,
  children: VNode | VNode[]
) => {
  const { onClick, tabIndex, disabled, title, identifier } = props;

  return (
    <button
      data-test-id={`${identifier}-aria-button`}
      onClick={() => onClick()}
      tabIndex={tabIndex}
      disabled={disabled}
      title={title}
      class={{
        [`${props.class}`]: !!props.class,
      }}>
      {children}
    </button>
  );
};
